import { useHandleResponse } from '@/utils/useHandleResponse';
import { useQuery } from '@tanstack/react-query';
import { App, AutoComplete, Button, Checkbox, Col, Form, Input, Row, Select, Spin, Tooltip } from 'antd';
import moment from 'moment';
import { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../../context/AuthProvider';
import { setAgents } from '../../../../contextRedux/Agent/AgentStore';
import { setCompanies } from '../../../../contextRedux/Company/CompanyStore';
import { setContracts } from '../../../../contextRedux/Contract/ContractStore';
import { setPmContracts } from '../../../../contextRedux/PMContract/PmContractStore';
import { updateField } from '../../../../contextRedux/RequestFile/RequestFileModel';
import { setRequestFileStatuses } from '../../../../contextRedux/RequestFileStatus/RequestFileStatusStore';
import { setSystemTypes } from '../../../../contextRedux/SystemType/SystemTypeStore';
import AgentService from '../../../../services/agent/AgentService';
import CompanyService from '../../../../services/company/CompanyService';
import ContractService from '../../../../services/contract/ContractService';
import PmContractService from '../../../../services/pmContract/PmContractService';
import RequestFileService from '../../../../services/requestFile/RequestFileService';
import SystemTypeService from '../../../../services/systemType/SystemTypeService';
import { RootState } from '../../../../store';
import './CreateRequestFile.css';

const { Option } = Select;

const CreateRequestFile: FC<any> = ({}) => {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();
  const dispatch = useDispatch();

  const profile = useAuth();

  const reduxRequestFile = useSelector((state: RootState) => state?.models?.requestFile);

  const statuses = useSelector((state: RootState) => state?.store?.requestFileStatus?.data);
  const companies = useSelector((state: RootState) => state?.store?.company?.data);
  const agents = useSelector((state: RootState) => state?.store?.agent?.data);
  const contracts = useSelector((state: RootState) => state?.store?.contract?.data);
  const pmContracts = useSelector((state: RootState) => state?.store?.pmContract?.data); //useSelector((_state: RootState) => []);  state?.store?.pmContract?.data);

  const [showAdditionalFields, setShowAdditionalFields] = useState<boolean>(false);

  const selectedCompany = useSelector((state: RootState) =>
    state?.store?.company?.data.find((c) => c.id == state?.models?.requestFile?.companyId),
  );

  const workPlaces = selectedCompany?.workPlaces;
  const [searchTerm, setSearchTerm] = useState<string>('');

  const initialValues = {
    number: useSelector((state: RootState) => state?.models?.requestFile?.number),
    company: useSelector((state: RootState) => state?.models?.requestFile?.company),
    companyId: useSelector((state: RootState) => state?.models?.requestFile?.companyId),
    workPlace: useSelector((state: RootState) => state?.models?.requestFile?.workPlace),
    workPlaceId: useSelector((state: RootState) => state?.models?.requestFile?.workPlaceId),
    contract: useSelector((state: RootState) => state?.models?.requestFile?.contract),
    contractId: useSelector((state: RootState) => state?.models?.requestFile?.contractId),
    requestFileSystemTypes: useSelector((state: RootState) => state?.models?.requestFile?.requestFileSystemTypes),
    agent: useSelector((state: RootState) => state?.models?.requestFile?.assignedTechnician),
    agentId: useSelector((state: RootState) => state?.models?.requestFile?.assignedTechnicianId),
    status: useSelector((state: RootState) => state?.models?.requestFile?.status),
    statusId: useSelector((state: RootState) => state?.models?.requestFile?.statusId),
    assignedTechnician: useSelector((state: RootState) => state?.models?.requestFile?.assignedTechnician),
    assignedTechnicianId: useSelector((state: RootState) => state?.models?.requestFile?.assignedTechnicianId),

    interventionDate: useSelector((state: RootState) => state?.models?.requestFile?.interventionDate),
    interventionStart: useSelector((state: RootState) => state?.models?.requestFile?.interventionStart),
    interventionEnd: useSelector((state: RootState) => state?.models?.requestFile?.interventionEnd),

    startingLatitude: useSelector((state: RootState) => state?.models?.requestFile?.startingLatitude),
    startingLongitude: useSelector((state: RootState) => state?.models?.requestFile?.startingLongitude),
    arrivalingLatitude: useSelector((state: RootState) => state?.models?.requestFile?.arrivalingLatitude),
    arrivalingLongitude: useSelector((state: RootState) => state?.models?.requestFile?.arrivalingLongitude),

    locationInfo: useSelector((state: RootState) => state?.models?.requestFile?.locationInfo),
    remarks: useSelector((state: RootState) => state?.models?.requestFile?.remarks),
    vehicle: useSelector((state: RootState) => state?.models?.requestFile?.vehicle),
    postBy: useSelector((state: RootState) => state?.models?.requestFile?.postBy),
    elapsedTime: useSelector((state: RootState) => state?.models?.requestFile?.elapsedTime),
    traveledKm: useSelector((state: RootState) => state?.models?.requestFile?.traveledKm),
    isUrgent: useSelector((state: RootState) => state?.models?.requestFile?.isUrgent),
    isActive: useSelector((state: RootState) => state?.models?.requestFile?.isActive),
  };

  const { data: _companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({ isAgentIncluded: true, isActive: true }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _status, isFetching: isRequestStatusLoading } = useQuery({
    queryKey: ['requestStatus'],
    queryFn: () =>
      RequestFileService.getStatuses().then((data) => {
        dispatch(setRequestFileStatuses(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setSystemTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _contracts,
    isFetching: isContractsLoading,
    refetch: searchContracts,
  } = useQuery({
    queryKey: ['contracts'],
    queryFn: () =>
      ContractService.search({
        workPlaceId: reduxRequestFile?.workPlaceId,
        isContractWorkPlaceIncluded: true,
        isActive: true,
      })
        .then((data) => {
          dispatch(setContracts(data?.response));
          return data?.response;
        })
        .catch((error) => {
          console.error('Error processing your request:', error);
        }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    enabled: !!reduxRequestFile?.workPlaceId,
  });

  const {
    data: _pmContracts,
    isFetching: isPmContractsLoading,
    refetch: searchPmContracts,
  } = useQuery({
    queryKey: ['pmContracts'],
    queryFn: () =>
      PmContractService.search({
        workPlaceId: reduxRequestFile?.workPlaceId,
        isContractWorkPlaceIncluded: true,
        isActive: true,
      })
        .then((data) => {
          dispatch(setPmContracts(data?.response));
          return data?.response;
        })
        .catch((error) => {
          console.error('Error processing your request:', error);
        }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    enabled: !!reduxRequestFile?.workPlaceId,
  });

  const {
    data: _agents,
    isFetching: isAgentsLoading,
    refetch: searchAgenst,
  } = useQuery({
    queryKey: ['agents'],
    queryFn: () =>
      AgentService.search({
        workPlaceId: reduxRequestFile?.workPlaceId,
        companyId: reduxRequestFile.companyId,
        isPersonalDataIncluded: true,
      })
        .then((data) => {
          dispatch(setAgents(data?.response));
          return data?.response;
        })
        .catch((error) => {
          console.error('Error processing your request:', error);
        }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    enabled: !!reduxRequestFile?.workPlaceId,
  });

  const repost = useCallback((record: any) => {
    RequestFileService.sendNotification(record?.id, { ...record, status: statuses.find((s) => s.id == 1) })
      .then((data) => {
        if (data.message) {
          message.success(data.message);
        } else {
          message.error('Failed tp send notification');
        }
      })
      .catch((error) => {
        console.error('Error processing your request:', error);
      });
  }, []);

  const onFinish = () => {
    RequestFileService.upsert(reduxRequestFile?.id, {
      ...reduxRequestFile,
      postBy: profile?.profile?.name,
      statusId: 1,
      isActive: true,
    })
      .then((data) => {
        if (data?.response) {
          handleResponse(data, 'Solicitarea ' + data?.response?.number);
          repost({ ...data?.response, telegramMessageId: null });
          form.resetFields();
          const clearedFields = Object.keys(form.getFieldsValue()).reduce((acc: any, key: any) => {
            acc[key] = undefined;
            return acc;
          }, {});
          form.setFieldsValue(clearedFields);

          form.setFieldsValue({ interventionDate: moment().format('YYYY-MM-DD') });
        } else {
          console.error('Received data is not in JSON format or JSON parsing failed.');
        }
      })
      .catch((error) => {
        console.error('Error processing your request:', error);
      });
  };

  const handleFilter = useCallback((inputValue: string, option: any) => {
    return option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  }, []);

  const handleSearchTermChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleContract = useCallback(
    (id: string): void => {
      const contract = [...contracts, ...pmContracts]?.find((c: any) => c.id === id);

      form.setFieldsValue({
        ...form.getFieldsValue(true),
        contract: contract,
        contractId: contract?.id,
      });

      const systemTypeNames = Array.from(
        new Set([
          contract?.contractWorkPlaceItems
            ?.filter((cwpi: any) => cwpi?.workPlace?.name == reduxRequestFile?.workPlace?.name)
            .flatMap((item: any) => item?.systemType?.name),
          contract?.pmContractWorkPlaceItems
            ?.filter((pcwpi: any) => pcwpi?.workPlace?.name == reduxRequestFile?.workPlace?.name)
            .flatMap((item: any) => item?.systemType?.name),
        ]),
      ).filter(Boolean);

      form.setFieldsValue({
        ...form.getFieldsValue(true),
        systemTypes: systemTypeNames.join(' / '),
      });

      dispatch(updateField({ field: 'contract', value: contract ?? null }));
      dispatch(updateField({ field: 'contractId', value: contract?.id ?? null }));
    },
    [reduxRequestFile, contracts, pmContracts],
  );

  const handleSelectRequestFileSystemTypes = useCallback(
    (selectedSystemTypes: string[]): void => {
      // Combine systems from the contract (installed systems) and global system types (other systems)
      const allSystemTypes = Array.from(
        new Map(
          [
            ...(reduxRequestFile?.contract?.contractWorkPlaceItems
              ?.map((item: any) => item.systemType) // Extract systemType objects
              .filter((systemType: any) => systemType && systemType?.name) || []), // Installed systems
            ...(_systemTypes || []), // Global system types
          ]
            .filter((systemType: any) => systemType && systemType?.name) // Filter out null or invalid systemTypes
            .map((systemType: any) => [systemType?.name, systemType]), // Create a Map where keys are systemType names
        ).values(), // Extract only the unique systemType objects from the Map
      );

      // Map selected system names to their corresponding systemType objects
      const updatedRequestFileSystemTypes = selectedSystemTypes?.map((systemTypeName: string) => {
        const systemType: any = allSystemTypes?.find((st: any) => st.name === systemTypeName); // Match by name
        return {
          id: systemType?.id || '',
          systemType: systemType,
          systemTypeId: systemType?.id,
          requestFileId: reduxRequestFile?.id,
        };
      });

      const systemTypeNames = updatedRequestFileSystemTypes?.map((item: any) => item?.systemType?.name);

      // Update form values with the new array of selected system types
      form.setFieldsValue({
        ...form.getFieldsValue(true),
        requestFileSystemTypes: systemTypeNames,
      });

      // Dispatch updates to Redux if needed
      dispatch(
        updateField({
          field: 'requestFileSystemTypes',
          value: updatedRequestFileSystemTypes ?? null,
        }),
      );
    },
    [reduxRequestFile, _systemTypes],
  );

  const handleAgent = useCallback(
    (id: string): void => {
      if (id === 'Adauga Solicitant') {
        form.setFieldsValue({
          agent: null,
          agentId: null,
          phone: '',
        });
        dispatch(updateField({ field: 'agent', value: null }));
        dispatch(updateField({ field: 'agentId', value: null }));
        setShowAdditionalFields(true);
        return;
      }

      const agent = agents?.find((agent: any) => agent?.id === id);
      setShowAdditionalFields(false);

      if (agent) {
        form.setFieldsValue({
          ...form.getFieldsValue(true),
          agent: agent,
          agentId: agent?.id,
          phone: Array.isArray(agent?.phone) && agent?.phone?.length > 0 ? agent?.phone[0] : '',
        });

        dispatch(updateField({ field: 'agent', value: agent }));
        dispatch(updateField({ field: 'agentId', value: agent?.id }));
      } else {
        form.setFieldsValue({
          agent: null,
          agentId: null,
          phone: '',
        });
        dispatch(updateField({ field: 'agent', value: null }));
        dispatch(updateField({ field: 'agentId', value: null }));
      }
    },
    [reduxRequestFile, agents],
  );

  const handleWorkPlace = useCallback(
    (value: string): void => {
      const workPlace = workPlaces?.find((w: any) => w?.name === value);

      form.setFieldsValue({
        ...form.getFieldsValue(true),
        workPlaceId: workPlace?.id,
        workPlace: workPlace,
      });

      dispatch(updateField({ field: 'workPlace', value: workPlace ?? null }));
      dispatch(updateField({ field: 'workPlaceId', value: workPlace?.id ?? null }));

      // Only trigger the search for contracts after the state is updated
      if (workPlace?.id) {
        // Delaying the search to ensure redux updates
        setTimeout(() => {
          searchContracts();
          searchPmContracts();
          searchAgenst();
        }, 0);
      }
    },
    [reduxRequestFile, workPlaces],
  );

  const handleSelectCompany = useCallback(
    (value: string): void => {
      const company = companies?.find((c) => c.name === value);
      form.setFieldsValue({
        ...form.getFieldsValue(true),
        companyId: company?.id,
        company: company,
      });

      dispatch(updateField({ field: 'company', value: company ?? null }));
      dispatch(updateField({ field: 'companyId', value: company?.id ?? null }));

      handleWorkPlace('');
      (handleContract(''), handleSelectRequestFileSystemTypes([]));
      handleAgent('');
    },
    [reduxRequestFile, companies],
  );

  // const handleFieldChange = useCallback(
  //   debounce((changedValues: any, _allValues: any) => {
  //     for (const key in changedValues) {
  //       dispatch(updateField({ field: key, value: changedValues[key] }));
  //       form.setFieldsValue({ [key]: changedValues[key] });
  //     }

  //     if (form.getFieldValue('contract')?.intervention === 0 && form.getFieldValue('isUrgent') === true) {
  //       message.warning(
  //         <>
  //           <div>
  //             <b>Numarul interventiilor corective de pe contract s-au terminat</b>
  //           </div>
  //           <div>
  //             <b>Tariful interventiei va fi de 180 lei/ora</b>
  //           </div>
  //         </>,
  //         5
  //       );
  //     }
  //   }, 300),
  //   []
  // );

  const handleSubmitAdditionalPerson = useCallback(() => {
    form
      .validateFields(['agentName', 'agentPhone', 'agentEmail', 'agentRole'])
      .then((values) => {
        AgentService.upsert('', {
          name: values?.agentName,
          phone: values?.agentPhone,
          email: values?.agentEmail,
          isActive: true,
          isDefaultOnInterventionFile: values.isDefaultOnInterventionFile ?? false,
          role: values?.agentRole,
          workPlaceId: reduxRequestFile?.workPlaceId,
          companyId: reduxRequestFile?.companyId,
        })
          .then((data) => {
            if (data) {
              if (data?.errors?.length > 0) {
                data.errors.forEach((item: any) => {
                  message.error(item);
                });
                return; // Stop further execution if there are errors
              }

              form.setFieldsValue({
                ...form.getFieldsValue(true),
                agentId: data?.response.id,
              });
              message.success('Agentul a fost adaugat cu succes');

              AgentService.search({
                workPlaceId: reduxRequestFile?.workPlaceId,
                companyId: reduxRequestFile.companyId,
              }).then((agentsData) => {
                dispatch(setAgents(agentsData?.response ?? []));
                dispatch(
                  updateField({
                    field: 'agent',
                    value: agentsData.response.find((a: any) => a.id === data?.response.id),
                  }),
                );
                dispatch(updateField({ field: 'agentId', value: data?.response.id }));

                setShowAdditionalFields(false);
              });
            } else {
              console.error('Received data is not in JSON format or JSON parsing failed.');
            }
          })
          .catch((error) => {
            message.error(`Error processing your request: ${error}`);
          });
      })
      .catch((errorInfo) => {
        message.error(`Validation failed: ${errorInfo}`);
      });
  }, [reduxRequestFile]);

  const formatDate = useCallback(
    (date: string): string => {
      const parsedDate = new Date(date);
      return parsedDate.toLocaleDateString('en-GB');
    },
    [reduxRequestFile],
  );

  if (
    isCompaniesLoading ||
    isRequestStatusLoading ||
    isSystemTypesLoading ||
    isContractsLoading ||
    isPmContractsLoading ||
    isAgentsLoading
  )
    return <Spin size='large' className='spinner'></Spin>;

  return (
    <>
      <div style={{ padding: '20px' }}>
        <div style={{ marginLeft: '20%', paddingBottom: '30px' }}>
          <h1>Dispecerat Solicitari</h1>
          <h2>Helion S.A.</h2>
        </div>
        <Form
          form={form}
          onFinish={onFinish}
          //onValuesChange={handleFieldChange}
          initialValues={initialValues}
          layout='vertical'
          style={{ margin: '20px 20px' }}
          className='create-request-form'
        >
          <Row gutter={16} style={{ justifyContent: 'center' }}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label='Client'
                name={['company', 'name']}
                rules={[{ required: true, message: 'Alegeti un client!' }]}
              >
                <AutoComplete
                  placeholder='Cauta firma...'
                  onSelect={handleSelectCompany}
                  onChange={handleSearchTermChange}
                  filterOption={handleFilter}
                  value={searchTerm}
                  allowClear
                >
                  {companies?.map((company, index) => (
                    <Option key={index} value={company?.name}>
                      {company?.name}
                    </Option>
                  ))}
                </AutoComplete>
              </Form.Item>
              <Form.Item
                label='Punct de lucru'
                name={['workPlace', 'name']}
                rules={[{ required: true, message: 'Alegeti un punct de lucru!' }]}
              >
                <AutoComplete
                  placeholder='Cauta punct de lucru'
                  onSelect={handleWorkPlace}
                  onChange={handleSearchTermChange}
                  filterOption={handleFilter}
                  value={searchTerm}
                  allowClear
                >
                  {workPlaces?.map((workPlace: any, index: any) => (
                    <Option key={index} value={workPlace?.name}>
                      {workPlace?.name}
                    </Option>
                  ))}
                </AutoComplete>
              </Form.Item>
              <Form.Item label='Contract' name={['contract', 'number']}>
                <Select allowClear onChange={(e: any) => handleContract(e)}>
                  {/* SM Contracts */}
                  {contracts?.map((contract: any) => {
                    const contractText = `SM: ${contract.number} - I: ${formatDate(
                      contract.dateStart,
                    )} - F: ${formatDate(contract.dateEnd)} - Sisteme: ${Array.from(
                      new Set(
                        contract?.contractWorkPlaceItems
                          ?.filter((cwpi: any) => cwpi?.workPlace?.name === reduxRequestFile?.workPlace?.name)
                          .flatMap((item: any) => item?.systemType?.name) || [],
                      ),
                    )
                      .filter(Boolean)
                      .join(', ')} - Interventii: ${
                      contract.hasUnlimitedInterventions == true ? 'nelimitate' : contract.intervention || 'N/A'
                    }`;

                    return (
                      <Option key={contract.id} value={contract.id}>
                        <Tooltip title={contractText}>
                          <span>{contractText}</span>
                        </Tooltip>
                      </Option>
                    );
                  })}

                  {/* PM Contracts */}
                  {pmContracts?.map((contract: any) => {
                    const pmContractText = `PM - ${contract.number} - I: ${formatDate(
                      contract.dateStart,
                    )} - F: ${formatDate(contract.dateEnd)} - Sisteme: ${Array.from(
                      new Set(
                        contract?.pmContractWorkPlaceItems
                          ?.filter((pcwpi: any) => pcwpi?.workPlace?.name === reduxRequestFile?.workPlace?.name)
                          .flatMap((item: any) => item?.systemType?.name) || [],
                      ),
                    )
                      .filter(Boolean)
                      .join(', ')}`;

                    return (
                      <Option key={contract.id} value={contract.id}>
                        <Tooltip title={pmContractText}>
                          <span>{pmContractText}</span>
                        </Tooltip>
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Form.Item
                label='Sistem'
                name='requestFileSystemTypes'
                rules={[{ required: true, message: 'Alegeti un contract!' }]}
              >
                <Select
                  mode='multiple'
                  onChange={handleSelectRequestFileSystemTypes}
                  value={reduxRequestFile?.requestFileSystemTypes?.map((item: any) => item.systemType?.name)}
                  defaultValue={[]}
                  allowClear
                >
                  <Select.OptGroup label='Sisteme Instalate'>
                    {Array.from(
                      new Map(
                        [
                          ...(reduxRequestFile?.contract?.contractWorkPlaceItems || [])
                            .filter((item: any) => item?.workPlace?.name === reduxRequestFile?.workPlace?.name) // Filter by workPlace name
                            .map((item: any) => item.systemType)
                            .filter((systemType: any) => systemType && systemType?.name)
                            .map((systemType: any) => [systemType?.name, systemType]),

                          ...(reduxRequestFile?.contract?.pmContractWorkPlaceItems || [])
                            .filter((item: any) => item?.workPlace?.name === reduxRequestFile?.workPlace?.name) // Filter by workPlace name
                            .map((item: any) => item.systemType)
                            .filter((systemType: any) => systemType && systemType?.name)
                            .map((systemType: any) => [systemType?.name, systemType]),
                        ], // Flatten both arrays into a single array of key-value pairs
                      ).values(),
                    ).map((systemType: any) => (
                      <Option key={systemType?.id} value={systemType?.name}>
                        {systemType?.name}
                      </Option>
                    ))}
                  </Select.OptGroup>

                  <Select.OptGroup label='Alte Sisteme'>
                    {Array.from(
                      new Map(
                        (_systemTypes || [])
                          .filter((systemType: any) => {
                            if (!systemType || !systemType?.name) return false;

                            // Collect names from both contractWorkPlaceItems and pmContractWorkPlaceItems
                            const existingNames = [
                              ...(reduxRequestFile?.contract?.contractWorkPlaceItems || []).map(
                                (item: any) => item.systemType?.name,
                              ),
                              ...(reduxRequestFile?.contract?.pmContractWorkPlaceItems || []).map(
                                (item: any) => item.systemType?.name,
                              ),
                            ];

                            return !existingNames.includes(systemType?.name);
                          })
                          .map((systemType: any) => [systemType?.name, systemType]),
                      ).values(),
                    ).map((systemType: any) => (
                      <Option key={systemType?.id} value={systemType?.name}>
                        {systemType?.name}
                      </Option>
                    ))}
                  </Select.OptGroup>
                </Select>
              </Form.Item>
              <Form.Item name='isUrgent'>
                <Checkbox
                  checked={reduxRequestFile?.isUrgent}
                  onChange={(e: any) => dispatch(updateField({ field: 'isUrgent', value: e.target.checked }))}
                  style={{ color: 'red' }}
                >
                  URGENTA
                </Checkbox>
              </Form.Item>
              <Form.Item
                label='Data interventiei'
                name='interventionDate'
                rules={[
                  {
                    required: true,
                    message: 'Alegeti o data pentru solicitare!',
                  },
                ]}
              >
                <Input
                  type='date'
                  onChange={(e: any) => dispatch(updateField({ field: 'interventionDate', value: e.target.value }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label='Nume Prenume Solicitant' name={['agent', 'name']}>
                <Select allowClear onChange={(e: any) => handleAgent(e)}>
                  {agents?.map((agent: any) => (
                    <Option key={agent?.id} value={agent?.user?.name}>
                      {agent?.name}
                    </Option>
                  ))}
                  {reduxRequestFile?.workPlaceId && (
                    <Option key={'Adauga Solicitant'} value={'Adauga Solicitant'}>
                      Adauga Solicitant
                    </Option>
                  )}
                </Select>
              </Form.Item>

              {showAdditionalFields && reduxRequestFile?.workPlaceId && (
                <div style={{ marginTop: 16 }}>
                  <Form.Item name='agentName' rules={[{ required: true, message: 'Introduceti numele!' }]}>
                    <Input placeholder='Nume' />
                  </Form.Item>
                  <Form.Item name='agentPhone' rules={[{ required: true, message: 'Introduceti numarul de telefon!' }]}>
                    <Input placeholder='Numar de telefon' />
                  </Form.Item>
                  <Form.Item name='agentEmail' rules={[{ required: true, message: 'Introduceti emailul!' }]}>
                    <Input placeholder='Email' />
                  </Form.Item>
                  <Form.Item name='agentRole' rules={[{ required: true, message: 'Introduceti rolul!' }]}>
                    <Input placeholder='Rol' />
                  </Form.Item>
                  <Button type='primary' onClick={handleSubmitAdditionalPerson}>
                    Adauga
                  </Button>
                </div>
              )}

              <Form.Item
                label='Telefon solicitant'
                name={['agent', 'phone']}
                rules={[
                  {
                    required: true,
                    message: 'Introduceti numarul de telefon al solicitantului!',
                  },
                ]}
              >
                <Input placeholder='07323232123' value={reduxRequestFile?.agent?.phone?.[0] ?? ''} />
              </Form.Item>
              <Form.Item
                label='Info locatie'
                name='locationInfo'
                rules={[
                  {
                    required: true,
                    message: 'Introduceti informatii despre locatie!',
                  },
                ]}
              >
                <Input
                  placeholder='Exemplu: Etajul 5, Cabinet nr 502'
                  onChange={(e: any) => dispatch(updateField({ field: 'locationInfo', value: e.target.value }))}
                />
              </Form.Item>
              <Form.Item
                label='Descrie problema'
                name='remarks'
                rules={[{ required: true, message: 'Descrieti problema!' }]}
              >
                <Input.TextArea
                  onChange={(e: any) => dispatch(updateField({ field: 'remarks', value: e.target.value }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} style={{ textAlign: 'center' }}>
              <Button type='primary' htmlType='submit'>
                Trimite solicitarea
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  );
};

export default CreateRequestFile;
